import { useQuery } from '@tanstack/react-query'
import { AdvertisersApi } from '@/services/advertisers/advertisersApi'
import { AuthService } from '@/services/core/auth'
import { isSuperAdmin } from '@/data/superAdmins'
import { useCurrentUser } from '../contexts/UserContext'
import { objectCrumbs, type Crumb, type ObjectAncestors } from '../components/objectCrumbs'

/**
 * The trail for a page, with the advertiser's owner team in front of it for
 * Adnuntius' own account — which works across every network and needs to see
 * whose team an advertiser belongs to.
 *
 * Everyone else gets the plain trail: within one team it names only itself.
 */
export function useObjectCrumbs(ancestors: ObjectAncestors, currentLabel: string): Crumb[] {
  const currentUser = useCurrentUser()
  const advertiserId = ancestors.advertiser?.id
  // The team is only on the advertiser, which a page further down the chain
  // does not load for itself.
  const enabled =
    isSuperAdmin(currentUser?.username) && !!advertiserId && AuthService.isAuthenticated()

  const { data: advertiser } = useQuery({
    queryKey: ['advertiser', advertiserId],
    queryFn: () => AdvertisersApi.getById(advertiserId!),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  const crumbs = objectCrumbs(ancestors, currentLabel)
  if (!enabled || !advertiser?.ownerTeamId || !advertiser.ownerTeamName) return crumbs

  return [
    { label: advertiser.ownerTeamName, href: `/admin/teams/${advertiser.ownerTeamId}` },
    ...crumbs,
  ]
}
